import React, { useEffect, type ReactElement } from 'react'
import ThemeInputDialog from 'common/ui/theme-input-dialog/ThemeInputDialog'
import { array, object, mixed } from 'yup'
import { useFormik } from 'formik'
import { useAppDispatch, useAppSelector } from 'store/hooks'
import { assign } from 'lodash'
import { editVendor } from 'features/vendor/vendor.service'
import { Checkbox, FormControlLabel, FormHelperText, Typography, useTheme } from '@mui/material'
import { TopicType } from 'common/tenders/tenders.types'
import cx from 'classnames'
import { Role } from 'common/utils/types'

import styles from './TopicsDialog.module.scss'

interface TopicsDialogProps {
  isOpen: boolean
  setIsOpen: React.Dispatch<React.SetStateAction<boolean>>
}

const topicsSchema = object({
  topics: array(mixed<TopicType>().oneOf(Object.values(TopicType)).required()).min(
    1,
    'Morate odabrati barem jednu oblast.',
  ),
})

const TopicsDialog: React.FC<TopicsDialogProps> = ({ isOpen, setIsOpen }): ReactElement => {
  const { palette } = useTheme()
  const dispatch = useAppDispatch()
  const { validationErrors, vendor, editVendorStatus } = useAppSelector((state) => state.vendor)
  const { userInfo } = useAppSelector((state) => state.auth)

  const formik = useFormik({
    validationSchema: topicsSchema,
    initialValues: {
      topics: vendor?.topics,
    },
    onSubmit: async (values) => {
      await dispatch(editVendor({ topics: values.topics }))
      setIsOpen(false)
    },
  })

  useEffect(() => {
    if (validationErrors?.length) {
      formik.setErrors(assign({}, ...validationErrors.map((err) => ({ [err.type]: err.message }))))
    }
  }, [validationErrors])

  return (
    <ThemeInputDialog
      open={isOpen}
      title="Oblasti rada"
      description="Potrebno je da izaberete oblasti rada za koje želite da primate ponude za tender. Ukoliko neku oblast ne izaberete nećete dobijati tendere za tu oblast. Na svom profilu u svakom trenutku možete promeniti izbor."
      status={editVendorStatus}
      onClose={() => {
        setIsOpen(false)
      }}
      confirmButtonText="snimi"
      declineButtonText="izađi"
      confirmButtonAction={() => {
        formik.handleSubmit()
      }}
      confirmButtonDisabled={!formik.isValid || !formik.dirty}
      PaperProps={{
        sx: {
          width: '850px',
        },
      }}
    >
      <form className={styles.container}>
        <FormControlLabel
          control={<Checkbox />}
          sx={{ marginLeft: '0', width: '100%' }}
          label={
            <div
              className={cx(styles.label, {
                [styles.services]: formik.values.topics?.includes(TopicType.SERVICE),
              })}
            >
              <Typography variant="title1">Usluge</Typography>
              <Typography variant="title5" color={palette.gray.main}>
                Reklama, trening i konsultacije
              </Typography>
            </div>
          }
          labelPlacement="start"
          disableTypography
          name="topics"
          value={TopicType.SERVICE}
          onChange={formik.handleChange}
          checked={!!formik.values.topics?.includes(TopicType.SERVICE)}
        />
        <FormControlLabel
          control={<Checkbox />}
          sx={{ marginLeft: '0', width: '100%' }}
          label={
            <div
              className={cx(styles.label, {
                [styles.software]: formik.values.topics?.includes(TopicType.SOFTWARE),
              })}
            >
              <Typography variant="title1">Softver</Typography>
              <Typography variant="title5" color={palette.gray.main}>
                Gotova rešenja (prodaja licenci), razvoj softvera i implementacija softvera
              </Typography>
            </div>
          }
          labelPlacement="start"
          disableTypography
          name="topics"
          value={TopicType.SOFTWARE}
          onChange={formik.handleChange}
          checked={formik.values.topics?.includes(TopicType.SOFTWARE) ?? false}
        />
        {userInfo?.role !== Role.consultant && (
          <FormControlLabel
            control={<Checkbox />}
            sx={{ marginLeft: '0', width: '100%' }}
            label={
              <div
                className={cx(styles.label, {
                  [styles.hardware]: formik.values.topics?.includes(TopicType.HARDWARE),
                })}
              >
                <Typography variant="title1">Hardver</Typography>
                <Typography variant="title5" color={palette.gray.main}>
                  Sve što jedan hardver podrazumeva
                </Typography>
              </div>
            }
            labelPlacement="start"
            disableTypography
            name="topics"
            value={TopicType.HARDWARE}
            onChange={formik.handleChange}
            checked={formik.values.topics?.includes(TopicType.HARDWARE) ?? false}
          />
        )}
        {formik.touched.topics && formik.errors.topics && (
          <FormHelperText error>{formik.errors.topics}</FormHelperText>
        )}
      </form>
    </ThemeInputDialog>
  )
}

export default TopicsDialog
